import React, { useEffect, useState } from 'react';
import './user-management.scss';

import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { Typography, Button, ListTable } from 'design_system/src';
import { Company } from 'models/companies/interfaces/companies.interface';
import { CompanyStatus } from 'models/companies/enums/companies.enums';
import {
	fetchCompanies,
	cancelCompany,
	updateCompany,
} from 'models/user-management/controllers/companies.controller';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import EditCompany from './edit/EditCompany';

function Companies() {
	const [companies, setCompanies] = useState<Company[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();
	const [openEditModal, setOpenEditModal] = useState<boolean>(false);

	// Fetch companies
	const loadCompanies = async () => {
		setLoading(true);
		try {
			const data = await fetchCompanies();
			setCompanies(data);
		} catch (error: any) {
			toasterNotification({
				msg: error?.message || 'Error loading companies',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadCompanies();
	}, []);

	const handleCreate = () => {
		setSelectedCompany(undefined);
		setOpenEditModal(true);
	};

	const handleEdit = (company: Company) => {
		setSelectedCompany(company);
		setOpenEditModal(true);
	};

	const handleCancel = async (company: Company) => {
		if (
			window.confirm(
				`Are you sure you want to cancel company "${company.name}"? This will change its status to cancelled.`,
			)
		) {
			try {
				await cancelCompany(company._id);
				toasterNotification({
					msg: 'Company cancelled successfully',
					toasterType: toasterOptions.success,
				});
				loadCompanies();
			} catch (error: any) {
				const errorMessage =
					extractErrorMessage(error) || 'Error cancelling company';
				toasterNotification({
					msg: errorMessage,
					toasterType: toasterOptions.error,
				});
			}
		}
	};

	const handleActivate = async (company: Company) => {
		if (
			window.confirm(
				`Are you sure you want to activate company "${company.name}"? This will change its status to active.`,
			)
		) {
			try {
				await updateCompany(company._id, { status: CompanyStatus.ACTIVE });
				toasterNotification({
					msg: 'Company activated successfully',
					toasterType: toasterOptions.success,
				});
				loadCompanies();
			} catch (error: any) {
				const errorMessage =
					extractErrorMessage(error) || 'Error activating company';
				toasterNotification({
					msg: errorMessage,
					toasterType: toasterOptions.error,
				});
			}
		}
	};

	const handleCloseModal = () => {
		setOpenEditModal(false);
		setSelectedCompany(undefined);
	};

	const handleSaveSuccess = () => {
		loadCompanies();
		handleCloseModal();
	};

	const cols = [
		{
			label: 'Name',
			attribute: 'name' as keyof Company,
		},
		{
			label: 'Email',
			attribute: 'email' as keyof Company,
		},
		{
			label: 'Status',
			render: (
				company: Company,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => {
				const status = company.status || CompanyStatus.ACTIVE;
				const statusLabels: Record<CompanyStatus, string> = {
					[CompanyStatus.ACTIVE]: 'Active',
					[CompanyStatus.TRIAL]: 'Trial',
					[CompanyStatus.CANCELLED]: 'Cancelled',
				};
				return (
					<BaseTypography>{statusLabels[status] || status}</BaseTypography>
				);
			},
		},
		{
			label: 'Tax ID',
			render: (
				company: Company,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => {
				// Backend sends taxId, check that first
				const taxId = (company as any).taxId || '-';
				return <BaseTypography>{taxId}</BaseTypography>;
			},
		},
		{
			label: 'Role',
			attribute: 'role' as keyof Company,
		},
		{
			label: 'Actions',
			render: (
				company: Company,
				index: number,
				BaseTypography: React.FC<{
					children?: React.ReactNode;
					className?: string;
				}>,
			) => (
				<div style={{ display: 'flex', gap: '8px' }}>
					<Button
						variant="outline"
						scale="mini"
						onClick={() => handleEdit(company)}
					>
						<Typography scale="xsmall" weight="400">
							Edit
						</Typography>
					</Button>
					{company.status === CompanyStatus.CANCELLED ? (
						<Button
							variant="outline"
							scale="mini"
							color="green"
							onClick={() => handleActivate(company)}
						>
							<Typography scale="xsmall" weight="400">
								Activate
							</Typography>
						</Button>
					) : (
						<Button
							variant="outline"
							scale="mini"
							color="red"
							onClick={() => handleCancel(company)}
						>
							<Typography scale="xsmall" weight="400">
								Cancel
							</Typography>
						</Button>
					)}
				</div>
			),
		},
	];

	return (
		<div className="user-management-container">
			<div className="user-management-header">
				<Typography scale="heading3" weight="600">
					Companies Management
				</Typography>
				<Button variant="principal" icon={<AddCircle />} onClick={handleCreate}>
					Create Company
				</Button>
			</div>

			{loading ? (
				<div style={{ padding: '24px', textAlign: 'center' }}>
					<Typography scale="medium" weight="400">
						Loading companies...
					</Typography>
				</div>
			) : (
				<ListTable
					selectId="_id"
					canSelect={false}
					data={companies}
					cols={cols}
				/>
			)}

			{openEditModal && (
				<EditCompany
					company={selectedCompany}
					onClose={handleCloseModal}
					onSave={handleSaveSuccess}
				/>
			)}
		</div>
	);
}

export default Companies;
